// 2) Third-party library imports
import {
    get,
    isEqual,
    keyBy,
    merge,
    uniqWith,
    values
} from 'lodash-es';
import {
    createAction,
    createSelector,
    createSlice,
    PayloadAction
} from '@reduxjs/toolkit';

// 3a) Constants
import {
    DETAILS_TAB,
    PAYMENT_PLANS_PAGE,
} from '../../config/tableAndPageConstants';
import {
    invoicesSortByOptions,
    invoicesStateFilterOptions,
} from '../../constants/invoicesSortAndFilters';
import {
    conversationTableFilterOptions,
    paymentPlansSortByOptions,
    paymentPlansStateFilterOptions,
    paymentScheduleSortByOptions,
} from '../../constants/paymentPlansSortAndFilters';

// 3c) Project-specific modules - components / types
import {
    CancelPaymentPlanRequestPayload,
    Conversation,
    GetConversationRequestPayload,
    GetPaymentPlanDataScheduleRequestPayload,
    GetPaymentPlansRequestPayload,
    PaymentPlan,
    PaymentPlanAddCommentRequestPayload,
    PaymentPlanConfig,
    PaymentPlanSchedule,
    PaymentPlansState,
    RequestManualCreateNewPaymentPlanPayload,
    RequestPaymentPlansPayload,
    ResendPaymentPlanApprovalRequestPayload,
    UpdatePaymentPlanScheduleRequestPayload
} from './types';
import { PageData } from '../common/types';
import {
    GetInvoicesRequestPayload,
    Invoice
} from '../invoices/types';
import type { RootState } from '../storeTypes';

// 3d) Utility functions / interfaces
import {
    DynamicObject,
    ResponseModalObject
} from '../../utils/commonInterfaces';

const actionPrefix = 'paymentPlan';

// Type-safe initialState!
export const initialState: PaymentPlansState = {
    loading: false,
    savePaymentPlanDataLoading: false,
    errorMessages: [],
    data: [],
    pageData: {
        pageSize: PAYMENT_PLANS_PAGE.pageSize,
        currentPage: 0,
        hasNextPage: false,
    },
    paymentPlanState: get(paymentPlansStateFilterOptions, `${0}.value`),
    filters: {},
    tableFilter: undefined,
    sortBy: paymentPlansSortByOptions[1].value, //1 for created date
    sortAscending: false, // Descending order by default
    activeData: {
        record: {},
        invoices: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.INVOICE_LIST_COMPLETE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {
                InvoiceNumber: '',
                Customer: '',
                CustomerCode: '',
                CreatedDateMin: '',
                CreatedDateMax: '',
                DueDateMin: '',
                DueDateMax: '',
                AmountType: '',
                AmountValue: '',
                AmountOperator: '',
            },
            invoiceState: get(invoicesStateFilterOptions, `${1}.value`),
            sortBy: invoicesSortByOptions[0].value, // 2 for Invoice number
            sortAscending: true,
        },
        schedule: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.PAYMENT_PLAN_SCHEDULE_LIST.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {},
            sortBy: paymentScheduleSortByOptions[0].value, // 2 for Invoice number
            sortAscending: true,
        },
        conversation: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.CONVERSATION_TIMELINE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {},
            conversationTableFilter: get(
                conversationTableFilterOptions,
                '0.value'
            ),
            sortBy: 'Created Date Time',
            sortAscending: false,
        },
        loading: false,
        selectedId: null,
        errorMessages: [],
    },
    paymentPlanConfigs: []
};

const paymentPlanSlice = createSlice({
    name: actionPrefix,
    initialState,
    reducers: {
        getPaymentPlansRequestAction: (
            state,
            _action: PayloadAction<GetPaymentPlansRequestPayload>
        ) => {
            state.loading = true;
        },
        getPaymentPlansSuccessAction: (
            state,
            action: PayloadAction<{
                data: PaymentPlan[];
                pageData: PageData;
                mergeData?: boolean;
            }>
        ) => {
            if (action.payload.mergeData === true) {
                state.data = values(
                    merge(
                        keyBy(state.data, 'Id'),
                        keyBy(action.payload.data, 'Id')
                    )
                );
            } else if (action.payload.pageData.currentPage === 0) {
                state.data = action.payload.data;
            } else {
                const existingData = state.data || [];
                state.data = uniqWith([...existingData, ...action.payload.data], isEqual);
            }

            state.loading = false;
            state.pageData = action.payload.pageData;
            state.errorMessages = [];
        },
        getPaymentPlansErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.loading = false;
            state.data = initialState.data;
            state.errorMessages = action.payload;
        },
        updatePaymentPlansSortByAndStateAction: (
            state,
            action: PayloadAction<{
                sortBy: GetPaymentPlansRequestPayload['sortBy'];
                sortAscending: boolean;
                paymentPlanState: string;
            }>
        ) => {
            state.sortBy = action.payload.sortBy;
            state.sortAscending = action.payload.sortAscending;
            state.paymentPlanState = action.payload.paymentPlanState;
        },
        updatePaymentPlansTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.tableFilter = action.payload;
        },
        clearPaymentPlansStateAllTableFiltersAction: (state) => {
            state.filters = initialState.filters;
            state.sortBy = initialState.sortBy;
            state.sortAscending = initialState.sortAscending;
            state.tableFilter = initialState.tableFilter;
            state.paymentPlanState = initialState.paymentPlanState;
        },
        clearPaymentPlansStateDataAction: (state) => {
            Object.assign(state, initialState);
        },
        setPaymentPlanSelectedIdRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    paymentPlanId: string | null;
                    callback: () => void;
                }>
            ) => {
                state.activeData.selectedId = initialState.activeData.selectedId;
            },
            prepare: (
                paymentPlanId: string | null,
                callback: () => void
            ) => ({
                payload: {
                    paymentPlanId,
                    callback
                }
            })
        },
        setPaymentPlanSelectedIdSuccessAction: (
            state,
            action: PayloadAction<string | null>
        ) => {
            state.activeData.selectedId = action.payload;
        },
        getPaymentPlanDataRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    paymentPlanId: string,
                    isUsingCloudImportType: boolean
                }>
            ) => {
                state.activeData.record = initialState.activeData.record;
                state.activeData.loading = true;
            },
            prepare: (
                paymentPlanId: string,
                isUsingCloudImportType: boolean
            ) => ({
                payload: {
                    paymentPlanId,
                    isUsingCloudImportType
                }
            })
        },
        getPaymentPlanDataSuccessAction: (
            state,
            action: PayloadAction<{ record: {} }>
        ) => {
            state.activeData.record = action.payload.record;
            state.activeData.loading = false;
            state.activeData.errorMessages = [];
        },
        getPaymentPlanDataErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.record = initialState.activeData.record;
            state.activeData.loading = false;
            state.activeData.errorMessages = action.payload;
        },
        clearPaymentPlanDataAction: (state) => {
            state.activeData = initialState.activeData;
        },
        getPaymentPlanInvoicesRequestAction: (
            state,
            _action: PayloadAction<{
                payload: GetInvoicesRequestPayload;
                region: string
            }>
        ) => {
            state.activeData.invoices.loading = true;
        },
        getPaymentPlanInvoicesSuccessAction: (
            state,
            action: PayloadAction<{
                data: Invoice[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.activeData.invoices.data = action.payload.data;
            } else {
                const existingData = state.activeData.invoices.data || [];
                state.activeData.invoices.data = uniqWith([...existingData, ...action.payload.data], isEqual);
            }

            state.activeData.invoices.loading = false;
            state.activeData.invoices.pageData = action.payload.pageData;
            state.activeData.invoices.errorMessages = [];
        },
        getPaymentPlanInvoicesErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.invoices.loading = false;
            state.activeData.invoices.data = initialState.activeData.invoices.data;
            state.activeData.invoices.errorMessages = action.payload;
        },
        updatePaymentPlanInvoicesFiltersAction: (
            state,
            action: PayloadAction<GetInvoicesRequestPayload['filters']>
        ) => {
            state.activeData.invoices.filters = action.payload;
        },
        updatePaymentPlanInvoicesSortByAndStateAction: (
            state,
            action: PayloadAction<{
                sortBy: GetInvoicesRequestPayload['sortBy'];
                sortAscending: boolean;
                invoiceState: string;
            }>
        ) => {
            state.activeData.invoices.sortBy = action.payload.sortBy;
            state.activeData.invoices.sortAscending = action.payload.sortAscending;
            state.activeData.invoices.invoiceState = action.payload.invoiceState;
        },
        updatePaymentPlanInvoicesTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.activeData.invoices.invoiceState = action.payload;
        },
        getPaymentPlanDataScheduleRequestAction: (
            state,
            _action: PayloadAction<GetPaymentPlanDataScheduleRequestPayload>
        ) => {
            state.activeData.schedule.loading = true;
        },
        getPaymentPlanDataScheduleSuccessAction: (
            state,
            action: PayloadAction<{
                data: PaymentPlanSchedule[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.activeData.schedule.data = action.payload.data;
            } else {
                const existingData = state.activeData.schedule.data || [];
                state.activeData.schedule.data = uniqWith([...existingData, ...action.payload.data], isEqual);
            }

            state.activeData.schedule.loading = false;
            state.activeData.schedule.pageData = action.payload.pageData;
            state.activeData.schedule.errorMessages = [];
        },
        getPaymentPlanDataScheduleErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.schedule.loading = false;
            state.activeData.schedule.data = initialState.activeData.schedule.data;
            state.activeData.schedule.errorMessages = action.payload;
        },
        updatePaymentPlanDataScheduleFiltersAction: (
            state,
            action: PayloadAction<GetPaymentPlanDataScheduleRequestPayload['filters']>
        ) => {
            state.activeData.schedule.filters = action.payload;
        },
        updatePaymentPlanDataScheduleSortByAndStateAction: (
            state,
            action: PayloadAction<{
                sortBy: GetPaymentPlanDataScheduleRequestPayload['sortBy'];
                sortAscending: boolean;
            }>
        ) => {
            state.activeData.schedule.sortBy = action.payload.sortBy;
            state.activeData.schedule.sortAscending = action.payload.sortAscending;
        },
        getPaymentPlanConversationRequestAction: (
            state,
            _action: PayloadAction<GetConversationRequestPayload>
        ) => {
            state.activeData.conversation.loading = true;
        },
        getPaymentPlanConversationSuccessAction: (
            state,
            action: PayloadAction<{
                data: Conversation[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.activeData.conversation.data = action.payload.data;
            } else {
                const existingData = state.activeData.conversation.data || [];
                state.activeData.conversation.data = uniqWith([...existingData, ...action.payload.data], isEqual);
            }

            state.activeData.conversation.loading = false;
            state.activeData.conversation.pageData = action.payload.pageData;
            state.activeData.conversation.errorMessages = [];
        },
        getPaymentPlanConversationErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.conversation.loading = false;
            state.activeData.conversation.data = initialState.activeData.conversation.data;
            state.activeData.conversation.errorMessages = action.payload;
        },
        updatePaymentPlanConversationFiltersAction: (
            state,
            action: PayloadAction<GetConversationRequestPayload['filters']>
        ) => {
            state.activeData.conversation.filters = action.payload;
        },
        updatePaymentPlanConversationTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.activeData.conversation.conversationTableFilter = action.payload;
        },
        updateConversationSortByAction: (
            state,
            action: PayloadAction<{
                sortBy: GetConversationRequestPayload['sortBy'];
                sortAscending: boolean;
            }>
        ) => {
            state.activeData.conversation.sortBy = action.payload.sortBy;
            state.activeData.conversation.sortAscending = action.payload.sortAscending;
        },
        updatePaymentPlansFiltersAction: (
            state,
            action: PayloadAction<GetPaymentPlansRequestPayload['filters']>
        ) => {
            state.filters = action.payload;
        },
        getPaymentPlanConfigRequestAction: (state) => {
            state.loading = true;
        },
        getPaymentPlanConfigSuccessAction: (
            state,
            action: PayloadAction<PaymentPlanConfig[]>
        ) => {
            state.paymentPlanConfigs = action.payload;
            state.loading = false;
        },
        getPaymentPlanConfigErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.loading = false;
            state.data = initialState.data;
            state.errorMessages = action.payload;
        },
        createPaymentPlanConfigRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    payload: DynamicObject;
                    callback: (response: ResponseModalObject) => void;
                }>
            ) => {
                state.savePaymentPlanDataLoading = true;
            },
            prepare: (
                payload: DynamicObject,
                callback: (response: ResponseModalObject) => void
            ) => ({
                payload: { payload, callback }
            })
        },
        createPaymentPlanConfigErrorAction: (
            state
        ) => {
            state.savePaymentPlanDataLoading = false;
        },
        createPaymentPlanConfigSuccessAction: (
            state
        ) => {
            state.savePaymentPlanDataLoading = false;
        },
        deletePaymentPlanConfigsRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    payload: { ConfigIds: string[] };
                    callback: (response: ResponseModalObject) => void;
                }>
            ) => {
                state.savePaymentPlanDataLoading = true;
            },
            prepare: (
                payload: { ConfigIds: string[] },
                callback: (response: ResponseModalObject) => void
            ) => ({
                payload: { payload, callback }
            })
        },
        deletePaymentPlanConfigsErrorAction: (
            state
        ) => {
            state.savePaymentPlanDataLoading = false;
        },
        deletePaymentPlanConfigsSuccessAction: (
            state
        ) => {
            state.savePaymentPlanDataLoading = false;
        },
    }
});

const { actions, reducer } = paymentPlanSlice;

// Actions
export const {
    getPaymentPlansRequestAction,
    getPaymentPlansSuccessAction,
    getPaymentPlansErrorAction,
    updatePaymentPlansSortByAndStateAction,
    updatePaymentPlansTableFilterAction,
    clearPaymentPlansStateAllTableFiltersAction,
    clearPaymentPlansStateDataAction,
    setPaymentPlanSelectedIdRequestAction,
    setPaymentPlanSelectedIdSuccessAction,
    getPaymentPlanDataRequestAction,
    getPaymentPlanDataSuccessAction,
    getPaymentPlanDataErrorAction,
    clearPaymentPlanDataAction,
    getPaymentPlanInvoicesRequestAction,
    getPaymentPlanInvoicesSuccessAction,
    getPaymentPlanInvoicesErrorAction,
    updatePaymentPlanInvoicesFiltersAction,
    updatePaymentPlanInvoicesSortByAndStateAction,
    updatePaymentPlanInvoicesTableFilterAction,
    getPaymentPlanDataScheduleRequestAction,
    getPaymentPlanDataScheduleSuccessAction,
    getPaymentPlanDataScheduleErrorAction,
    updatePaymentPlanDataScheduleFiltersAction,
    updatePaymentPlanDataScheduleSortByAndStateAction,
    getPaymentPlanConversationRequestAction,
    getPaymentPlanConversationSuccessAction,
    getPaymentPlanConversationErrorAction,
    updatePaymentPlanConversationFiltersAction,
    updatePaymentPlanConversationTableFilterAction,
    updateConversationSortByAction,
    updatePaymentPlansFiltersAction,
    getPaymentPlanConfigRequestAction,
    getPaymentPlanConfigSuccessAction,
    getPaymentPlanConfigErrorAction,
    createPaymentPlanConfigRequestAction,
    createPaymentPlanConfigErrorAction,
    createPaymentPlanConfigSuccessAction,
    deletePaymentPlanConfigsRequestAction,
    deletePaymentPlanConfigsErrorAction,
    deletePaymentPlanConfigsSuccessAction
} = actions;

export const paymentPlanAddCommentRequestAction = createAction(
    `${actionPrefix}/paymentPlanAddCommentRequestAction`,
    function prepare(payload: PaymentPlanAddCommentRequestPayload) {
        return {
            payload
        };
    }
);

export const updatePaymentPlanScheduleRequestAction = createAction(
    `${actionPrefix}/updatePaymentPlanScheduleRequestAction`,
    function prepare(
        payload: UpdatePaymentPlanScheduleRequestPayload,
        callback: () => void
    ) {
        return {
            payload: { payload, callback },
        };
    }
);

export const createPaymentPlanAction = createAction(
    `${actionPrefix}/createPaymentPlanAction`,
    function prepare(
        payload: PaymentPlan,
        callback: (response: DynamicObject) => void
    ) {
        return {
            payload: { payload, callback },
        };
    }
);

export const fetchPaymentPlanCommunicationDetail = createAction(
    `${actionPrefix}/fetchPaymentPlanCommunicationDetail`,
    function prepare(
        communicationId: string,
        applyDelay: boolean,
        callback: (response: DynamicObject) => void
    ) {
        return {
            payload: { communicationId, applyDelay, callback },
        };
    }
);

export const requestPaymentPlansAction = createAction(
    `${actionPrefix}/requestPaymentPlansAction`,
    function prepare(
        payload: RequestPaymentPlansPayload,
        callback: (response: { IsSuccess: boolean, PaymentPlans: PaymentPlan[], Messages?: string[] }) => void
    ) {
        return {
            payload: { payload, callback },
        };
    }
);

export const actionPaymentPlanRequestAction = createAction(
    `${actionPrefix}/actionPaymentPlanRequestAction`,
    function prepare(
        payload: {}
    ) {
        return {
            payload
        };
    }
);

export const checkPaymentPlansActionStateAction = createAction(
    `${actionPrefix}/checkPaymentPlansActionStateAction`,
    function prepare(
        payload: {}
    ) {
        return {
            payload
        };
    }
);

export const updatePaymenPlanScheduleRequestAction = createAction(
    `${actionPrefix}/updatePaymenPlanScheduleRequestAction`,
    function prepare(
        payload: UpdatePaymentPlanScheduleRequestPayload,
        callback: () => void
    ) {
        return {
            payload: {
                payload,
                callback
            },
        };
    }
);

export const actionOrganisationPaymentPlanRequestAction = createAction(
    `${actionPrefix}/actionOrganisationPaymentPlanRequestAction`,
    function prepare(
        payload: {}
    ) {
        return {
            payload
        };
    }
);

export const cancelPaymentPlanRequestAction = createAction(
    `${actionPrefix}/cancelPaymentPlanRequestAction`,
    function prepare(
        payload: CancelPaymentPlanRequestPayload
    ) {
        return {
            payload
        };
    }
);

export const actionPaymentPlanTasks = createAction(
    `${actionPrefix}/actionPaymentPlanTasks`,
    function prepare(
        payload: DynamicObject
    ) {
        return {
            payload
        };
    }
);

export const actionOrganisationPaymentPlanTasks = createAction(
    `${actionPrefix}/actionOrganisationPaymentPlanTasks`,
    function prepare(
        payload: DynamicObject
    ) {
        return {
            payload
        };
    }
);

export const manualCreateNewPaymentPlanRequestAction = createAction(
    `${actionPrefix}/manualCreateNewPaymentPlanRequestAction`,
    function prepare(
        payload: RequestManualCreateNewPaymentPlanPayload
    ) {
        return {
            payload
        };
    }
);

export const resendPaymentPlanApprovalRequestAction = createAction(
    `${actionPrefix}/resendPaymentPlanApprovalRequestAction`,
    function prepare(
        payload: ResendPaymentPlanApprovalRequestPayload
    ) {
        return {
            payload
        };
    }
);

export const getNumberOfInstalmentsRequestAction = createAction(
    `${actionPrefix}/getNumberOfInstalmentsRequestAction`,
    function prepare(
        paymentPlanIds: string[],
        callback: (response: ResponseModalObject) => void
    ) {
        return {
            payload: {
                paymentPlanIds,
                callback
            },
        };
    }
);

// Selectors
export const getPaymentPlansState = (state: RootState) => state.paymentPlans;
export const getPaymentPlansPageData = createSelector(
    getPaymentPlansState,
    (paymentPlans) => paymentPlans.pageData
);
export const getPaymentPlanData = createSelector(
    getPaymentPlansState,
    (paymentPlans) =>  paymentPlans.activeData
);
export const getPaymentPlanDataSchedule = createSelector(
    getPaymentPlanData,
    (paymentPlanData) => paymentPlanData.schedule
);
export const getPaymentPlanSelectedId = createSelector(
    getPaymentPlanData,
    (paymentPlanData) => paymentPlanData.selectedId
);

// Reducer
export default reducer;